import { countries } from "@redotech/locale/countries";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoTextareaInputField } from "@redotech/redo-web/arbiter-components/input/redo-textarea-input-field";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { assertNever } from "@redotech/util/type";
import { memo } from "react";
import { MaybeDisplayHtml } from "../portal-four/returns/components/maybe-display-html";
import { Footer } from "./footer";
import { Header } from "./header";
import { GiftModalPage, useGift } from "./use-gift";
export const Gift = memo(function Gift({ onBack }: { onBack: () => void }) {
  const {
    texts,
    inputFields,
    inputFieldInfos,
    findOrderClicked,
    findOrderDisabled,
    findOrderLoad,
    showMissingBuyerInfoButton,
    modal,
  } = useGift();
  return (
    <Flex flexDirection="column" gap="3xl">
      <Header
        leftAlign
        logo={undefined}
        merchantName={texts.merchantName}
        onBack={onBack}
        subtitle={texts.subtitle}
        title={texts.title}
      />

      <Flex flexDirection="column" gap="xl">
        <Text fontSize="md" fontWeight="semibold">
          {texts.buyerInfo}
        </Text>

        <RedoTextInput
          description={inputFieldInfos.buyerEmailOrZip.errorDescription}
          label={inputFieldInfos.buyerEmailOrZip.label}
          setValue={inputFields.inputs.buyerEmailOrZip.setValue}
          state={
            inputFieldInfos.buyerEmailOrZip.errorDescription
              ? "error"
              : undefined
          }
          value={inputFields.inputs.buyerEmailOrZip.value}
        />
        <RedoTextInput
          description={inputFieldInfos.resourceId.errorDescription}
          label={inputFieldInfos.resourceId.label}
          setValue={inputFields.inputs.resourceId.setValue}
          state={
            inputFieldInfos.resourceId.errorDescription ? "error" : undefined
          }
          value={inputFields.inputs.resourceId.value}
        />
        {showMissingBuyerInfoButton && (
          <Flex justify="flex-start">
            <RedoButton
              hierarchy="link_underline"
              onClick={modal.openModal}
              text={texts.missingBuyerInfoButton}
            />
          </Flex>
        )}
      </Flex>
      <Flex flexDirection="column" gap="xl">
        <Text fontSize="md" fontWeight="semibold">
          {texts.yourInfo}
        </Text>
        <Flex flexDirection="row">
          <RedoTextInput
            description={inputFieldInfos.firstName.errorDescription}
            fullWidth
            label={inputFieldInfos.firstName.label}
            setValue={inputFields.inputs.firstName.setValue}
            state={
              inputFieldInfos.firstName.errorDescription ? "error" : undefined
            }
            value={inputFields.inputs.firstName.value}
          />
          <RedoTextInput
            description={inputFieldInfos.lastName.errorDescription}
            fullWidth
            label={inputFieldInfos.lastName.label}
            setValue={inputFields.inputs.lastName.setValue}
            state={
              inputFieldInfos.lastName.errorDescription ? "error" : undefined
            }
            value={inputFields.inputs.lastName.value}
          />
        </Flex>
        <RedoTextInput
          description={inputFieldInfos.email.errorDescription}
          label={inputFieldInfos.email.label}
          setValue={inputFields.inputs.email.setValue}
          state={inputFieldInfos.email.errorDescription ? "error" : undefined}
          value={inputFields.inputs.email.value}
        />
        <RedoTextInput
          description={inputFieldInfos.phoneNumber.errorDescription}
          label={inputFieldInfos.phoneNumber.label}
          setValue={inputFields.inputs.phoneNumber.setValue}
          state={
            inputFieldInfos.phoneNumber.errorDescription ? "error" : undefined
          }
          value={inputFields.inputs.phoneNumber.value}
        />
        <RedoTextInput
          description={inputFieldInfos.street1.errorDescription}
          label={inputFieldInfos.street1.label}
          setValue={inputFields.inputs.street1.setValue}
          state={inputFieldInfos.street1.errorDescription ? "error" : undefined}
          value={inputFields.inputs.street1.value}
        />
        <RedoTextInput
          description={inputFieldInfos.street2.errorDescription}
          label={inputFieldInfos.street2.label}
          setValue={inputFields.inputs.street2.setValue}
          state={inputFieldInfos.street2.errorDescription ? "error" : undefined}
          value={inputFields.inputs.street2.value}
        />
        <Flex flexDirection="row">
          <RedoTextInput
            description={inputFieldInfos.city.errorDescription}
            label={inputFieldInfos.city.label}
            setValue={inputFields.inputs.city.setValue}
            state={inputFieldInfos.city.errorDescription ? "error" : undefined}
            value={inputFields.inputs.city.value}
          />
          <RedoTextInput
            description={inputFieldInfos.state.errorDescription}
            label={inputFieldInfos.state.label}
            setValue={inputFields.inputs.state.setValue}
            state={inputFieldInfos.state.errorDescription ? "error" : undefined}
            value={inputFields.inputs.state.value}
          />
          <RedoTextInput
            description={inputFieldInfos.zip.errorDescription}
            label={inputFieldInfos.zip.label}
            setValue={inputFields.inputs.zip.setValue}
            state={inputFieldInfos.zip.errorDescription ? "error" : undefined}
            value={inputFields.inputs.zip.value}
          />
        </Flex>

        <RedoSingleSelectDropdownInput
          label={inputFieldInfos.country.label}
          options={countries.map((country) => ({
            type: "text",
            id: country.name,
            text: country.name,
            value: country.name,
          }))}
          optionSelected={function (value): void {
            inputFields.inputs.country.setValue(value.item.value);
          }}
          selectedItem={{
            type: "text",
            id: inputFields.inputs.country.value,
            text: inputFields.inputs.country.value,
            value: inputFields.inputs.country.value,
          }}
        />
      </Flex>
      <RedoButton
        disabled={findOrderDisabled}
        hierarchy="primary"
        onClick={() => findOrderClicked()}
        size="lg"
        text={texts.findOrder}
        theme="normal"
      />
      {findOrderLoad.error ? (
        <Flex align="center" flexDirection="column">
          <Text fontSize="xxs" textColor="error">
            {findOrderLoad.error?.message ||
              "Something went wrong, please try again."}
          </Text>
        </Flex>
      ) : null}
      <Footer />

      {modal.isOpen && (
        <RedoModal
          modalSize="sm"
          onModalCloseRequested={modal.closeModal}
          primaryButton={{
            text: texts.modal.primaryButton,
            size: "sm",
            onClick: modal.primaryButton.onClick,
            disabled: modal.primaryButton.disabled,
            pending: modal.primaryButton.pending,
          }}
          secondaryButton={
            modal.secondaryButton.show
              ? {
                  text: texts.modal.secondaryButton,
                  onClick: modal.secondaryButton.onClick,
                  disabled: modal.secondaryButton.disabled,
                  size: "sm",
                }
              : undefined
          }
          title={texts.modal.title}
        >
          {(() => {
            switch (modal.page) {
              case GiftModalPage.GIFT_RECEIPT_REQUEST:
                return (
                  <Flex flexDirection="column" gap="lg">
                    <Text fontSize="sm" textColor="tertiary">
                      <MaybeDisplayHtml html={texts.modal.subtitle} />
                    </Text>

                    <RedoTextInput
                      fullWidth
                      label={texts.modal.emailLabel}
                      placeholder={texts.modal.emailPlaceholder}
                      setValue={modal.setContactEmail}
                      value={modal.contactEmail}
                    />

                    <RedoTextareaInputField
                      autoExpand
                      fullWidth
                      label={texts.modal.informationLabel}
                      placeholder={texts.modal.informationPlaceHolder}
                      setValue={modal.setGiftDetails}
                      value={modal.giftDetails}
                    />
                  </Flex>
                );
              case GiftModalPage.GIFT_RECEIPT_REQUEST_SUCCESS:
                return (
                  <Flex flexDirection="column" gap="lg">
                    <Text fontSize="sm" textColor="tertiary">
                      {texts.modal.subtitle}
                    </Text>
                  </Flex>
                );
              default:
                assertNever(modal.page);
            }
          })()}
        </RedoModal>
      )}
    </Flex>
  );
});
